/**
 * InputManager — input fan-in for keyboard, gamepad, and touch.
 *
 * Keyboard remains the default. Gamepads are live for solo and two-pad co-op.
 * Touch is a solo-only overlay aimed at browser/mobile play.
 */
import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";
import {
  CompositeInputSource,
  GamepadInput,
  InputSource,
  KeyboardInput,
  NullInputSource,
  TouchInput,
} from "./InputSource";
import { SaveSystem } from "./SaveSystem";
import { SCREEN_RIGHT_WORLD_SIGN } from "../config/GameConfig";

export interface InputBindings {
  keyboard: {
    p1Left: string;
    p1Right: string;
    p1Jump: string;
    p1Ability: string;
    p2Left: string;
    p2Right: string;
    p2Jump: string;
    p2Ability: string;
  };
  gamepad: {
    jump: number;
    ability: number;
  };
}

export const DEFAULT_BINDINGS: InputBindings = {
  keyboard: {
    p1Left: "a",
    p1Right: "d",
    p1Jump: " ",
    p1Ability: "f",
    p2Left: "arrowleft",
    p2Right: "arrowright",
    p2Jump: "enter",
    p2Ability: "/",
  },
  gamepad: {
    jump: 0, // Button A / Cross
    ability: 1, // Button B / Circle
  },
};

/** moveX axis config where `right` moves toward screen-right. */
function moveAxis(left: string, right: string): { negative: string[]; positive: string[] } {
  return SCREEN_RIGHT_WORLD_SIGN > 0
    ? { negative: [left], positive: [right] }
    : { negative: [right], positive: [left] };
}
/** Stick right (+1) should also mean screen-right. */
const GAMEPAD_INVERT_X = SCREEN_RIGHT_WORLD_SIGN < 0;

export class InputManager {
  private keys: Record<string, boolean> = {};
  private _activePlayer: 1 | 2 = 1;
  private activeBindings: InputBindings = { ...DEFAULT_BINDINGS };
  // Default co-op: Minna = WASD/Space/F, Basil = Arrows/Shift|Enter/comma.
  // Press F2 to toggle to solo mode where Tab switches which cat is
  // active. Default was solo until 2026-04-17; the canon promises
  // True Simultaneous Co-Op as the core mechanic, so the default now
  // matches canon.
  private _soloMode = false;

  // Movement keys map to world X through SCREEN_RIGHT_WORLD_SIGN (see
  // VIEW.MIRROR_X in GameConfig): "right" always moves the cat toward
  // screen-right, whichever world direction that is.
  private keyboardP1Movement = new KeyboardInput({
    axes: {
      moveX: moveAxis("a", "d"),
    },
    buttons: {
      jump: [" "],
      ability: ["f"],
    },
  });

  private keyboardP2Movement = new KeyboardInput({
    axes: {
      moveX: moveAxis("arrowleft", "arrowright"),
    },
    buttons: {
      jump: ["shift", "enter"],
      ability: ["/"],
    },
  });

  private keyboardAbility = new KeyboardInput({
    buttons: {
      tab: ["tab"],
      q: ["q"],
      e: ["e"],
      f: ["f"],
      g: ["g"],
      n: ["n"],
      m: ["m"],
      r: ["r"],
      "1": ["1"],
      "2": ["2"],
      "3": ["3"],
      "4": ["4"],
      "5": ["5"],
      ",": [","],
    },
  });

  private gamepad0Movement = new GamepadInput(0, {
    axes: { moveX: { index: 0, deadzone: 0.18, invert: GAMEPAD_INVERT_X } },
    buttons: { jump: [0], ability: [1] },
  });

  private gamepad1Movement = new GamepadInput(1, {
    axes: { moveX: { index: 0, deadzone: 0.18, invert: GAMEPAD_INVERT_X } },
    buttons: { jump: [0], ability: [1] },
  });

  private gamepad0Minna = new GamepadInput(0, {
    buttons: {
      // Making Biscuits: either trigger starts kneading near an Elder
      // anchor, then LT/RT alternate left/right paw taps.
      b: [6, 7],
      a: [6],
      d: [7],
      q: [2],
      e: [1],
      f: [5],
      r: [3],
      tab: [8],
      m: [10],
      g: [11],
      n: [11],
    },
  });

  private gamepad0Basil = new GamepadInput(0, {
    buttons: {
      "1": [2],
      "2": [3],
      "3": [4],
      "5": [5],
      ",": [6],
      tab: [8],
      f: [10],
      m: [10],
      g: [11],
      n: [11],
    },
  });

  private gamepad1Basil = new GamepadInput(1, {
    buttons: {
      "1": [2],
      "2": [3],
      "3": [4],
      "5": [5],
      ",": [6],
      m: [6],
      n: [7],
    },
  });

  private touch = new TouchInput();
  private idleSource = new NullInputSource();

  private bus: any;
  private isGamepadActive = false;

  constructor(bus?: any) {
    this.bus = bus ?? gameEvents;
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("gamepadconnected", this.onGamepadConnected);
    window.addEventListener("gamepaddisconnected", this.onGamepadDisconnected);
    this.touch.setContext(this._activePlayer, this._soloMode);

    // Initial gamepad scan
    if (typeof navigator !== "undefined" && typeof navigator.getGamepads === "function") {
      try {
        const pads = navigator.getGamepads();
        if (pads && pads.some(p => p && p.connected)) {
          this.setGamepadActive(true);
        }
      } catch (e) {
        // ignore
      }
    }
    // Load custom bindings
    const saved = SaveSystem.getInstance().loadBindings();
    if (saved) {
      this.applyBindings(saved);
    }
  }

  public get bindings(): InputBindings {
    return this.activeBindings;
  }

  public applyBindings(bindings: any): void {
    if (!bindings) return;

    const norm = (key: any) => {
      if (typeof key !== "string") return key;
      let k = key.toLowerCase();
      if (k.startsWith("key")) k = k.substring(3);
      if (k.startsWith("digit")) k = k.substring(5);
      return k;
    };

    const normBtn = (btn: any): number => {
      if (typeof btn === "number") return btn;
      if (typeof btn !== "string") return 0;
      const b = btn.toLowerCase();
      if (b === "buttona" || b === "cross" || b === "a") return 0;
      if (b === "buttonb" || b === "circle" || b === "b") return 1;
      if (b === "buttonx" || b === "square" || b === "x") return 2;
      if (b === "buttony" || b === "triangle" || b === "y") return 3;
      return 0;
    };

    const keyboard = {
      p1Left: norm(bindings.keyboard?.p1Left || bindings.keyboard?.left || "a"),
      p1Right: norm(bindings.keyboard?.p1Right || bindings.keyboard?.right || "d"),
      p1Jump: norm(bindings.keyboard?.p1Jump || bindings.keyboard?.jump || " "),
      p1Ability: norm(bindings.keyboard?.p1Ability || bindings.keyboard?.purr || "f"),
      p2Left: norm(bindings.keyboard?.p2Left || "arrowleft"),
      p2Right: norm(bindings.keyboard?.p2Right || "arrowright"),
      p2Jump: norm(bindings.keyboard?.p2Jump || "enter"),
      p2Ability: norm(bindings.keyboard?.p2Ability || "/"),
    };

    const gamepad = {
      jump: normBtn(bindings.gamepad?.jump ?? 0),
      ability: normBtn(bindings.gamepad?.ability ?? bindings.gamepad?.purr ?? 1),
    };

    this.activeBindings = { keyboard, gamepad };

    // Re-initialize keyboard movement configs
    this.keyboardP1Movement = new KeyboardInput({
      axes: {
        moveX: moveAxis(keyboard.p1Left, keyboard.p1Right),
      },
      buttons: {
        jump: [keyboard.p1Jump],
        ability: [keyboard.p1Ability],
      },
    });

    this.keyboardP2Movement = new KeyboardInput({
      axes: {
        moveX: moveAxis(keyboard.p2Left, keyboard.p2Right),
      },
      buttons: {
        jump: [keyboard.p2Jump],
        ability: [keyboard.p2Ability],
      },
    });

    // Re-initialize keyboard ability config
    this.keyboardAbility = new KeyboardInput({
      buttons: {
        tab: ["tab"],
        q: ["q"],
        e: ["e"],
        f: [keyboard.p1Ability],
        g: ["g"],
        n: ["n"],
        m: ["m"],
        r: ["r"],
        "1": ["1"],
        "2": ["2"],
        "3": ["3"],
        "4": ["4"],
        "5": ["5"],
        ",": [","],
      },
    });

    // Re-initialize gamepad movement configs
    this.gamepad0Movement = new GamepadInput(0, {
      axes: { moveX: { index: 0, deadzone: 0.18, invert: GAMEPAD_INVERT_X } },
      buttons: { jump: [gamepad.jump], ability: [gamepad.ability] },
    });
    this.gamepad1Movement = new GamepadInput(1, {
      axes: { moveX: { index: 0, deadzone: 0.18, invert: GAMEPAD_INVERT_X } },
      buttons: { jump: [gamepad.jump], ability: [gamepad.ability] },
    });

    // Save bindings
    SaveSystem.getInstance().saveBindings(this.activeBindings);
  }

  public setGamepadActive(active: boolean): void {
    if (this.isGamepadActive === active) return;
    this.isGamepadActive = active;
    this.bus.emit(GameEvents.HUD_GLYPHS_CHANGED, { controllerActive: active });
  }

  private onGamepadConnected = (): void => {
    this.setGamepadActive(true);
  };

  private onGamepadDisconnected = (): void => {
    if (typeof navigator !== "undefined" && typeof navigator.getGamepads === "function") {
      try {
        const pads = navigator.getGamepads();
        const anyConnected = pads && pads.some(p => p && p.connected);
        this.setGamepadActive(anyConnected || false);
        return;
      } catch (e) {}
    }
    this.setGamepadActive(false);
  };

  get activePlayer(): 1 | 2 {
    return this._activePlayer;
  }

  /**
   * Directly set the active player. Bypasses the Tab-key flow used in
   * normal gameplay — consumed by the QA surface so tests don't race the
   * keyboard-event → justPressed propagation chain. No-op outside DEV.
   */
  setActivePlayer(player: 1 | 2): void {
    if (this._activePlayer === player) return;
    this._activePlayer = player;
    this.touch.setContext(this._activePlayer, this._soloMode);
    gameEvents.emit(GameEvents.PLAYER_SWITCHED, this._activePlayer);
  }

  get soloMode(): boolean {
    return this._soloMode;
  }

  set soloMode(value: boolean) {
    this._soloMode = value;
    this.touch.setContext(this._activePlayer, this._soloMode);
    gameEvents.emit(GameEvents.INPUT_MODE_CHANGED, { solo: this._soloMode });
  }

  update(): void {
    this.keyboardP1Movement.update(this.keys);
    this.keyboardP2Movement.update(this.keys);
    this.keyboardAbility.update(this.keys);
    this.gamepad0Movement.update();
    this.gamepad1Movement.update();
    this.gamepad0Minna.update();
    this.gamepad0Basil.update();
    this.gamepad1Basil.update();
    this.touch.update();

    const switchRequested = this.keyboardAbility.justPressed("tab")
      || this.gamepad0Minna.justPressed("tab")
      || this.gamepad0Basil.justPressed("tab")
      || this.touch.justPressed("switch");
    if (switchRequested && this._soloMode) {
      this._activePlayer = this._activePlayer === 1 ? 2 : 1;
      this.touch.setContext(this._activePlayer, this._soloMode);
      gameEvents.emit(GameEvents.PLAYER_SWITCHED, this._activePlayer);
    }
  }

  getInput(player: 1 | 2): InputSource {
    return new CompositeInputSource([
      player === 1 ? this.keyboardP1Movement : this.keyboardP2Movement,
      player === 1 ? this.gamepad0Movement : this.gamepad1Movement,
    ]);
  }

  getActiveInput(): InputSource {
    return new CompositeInputSource([
      this.keyboardP1Movement,
      this.gamepad0Movement,
      this.touch,
    ]);
  }

  getAbilityInput(): InputSource {
    if (this._soloMode) {
      return new CompositeInputSource([
        this.keyboardAbility,
        this._activePlayer === 1 ? this.gamepad0Minna : this.gamepad0Basil,
        this.touch,
      ]);
    }

    return new CompositeInputSource([
      this.keyboardAbility,
      this.gamepad0Minna,
      this.gamepad1Basil,
    ]);
  }

  getIdleInput(): InputSource {
    return this.idleSource;
  }

  dispose(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("gamepadconnected", this.onGamepadConnected);
    window.removeEventListener("gamepaddisconnected", this.onGamepadDisconnected);
    this.touch.dispose();
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    const key = this.normalizeKey(e.key);
    if (["arrowup", "arrowdown", "arrowleft", "arrowright", " ", "tab", "/"].includes(key)) {
      e.preventDefault();
    }
    // F2 toggles local-coop mode on/off. In solo mode the active
    // player (togglable via Tab) receives input; in coop mode both
    // keyboards are live concurrently (WASD + Space = Player 1 /
    // Minna, Arrows + Shift/Enter = Player 2 / Basil). Canon promises
    // local co-op but the prior default had no runtime toggle wired.
    if (key === "f2" && !this.keys[key]) {
      this.soloMode = !this._soloMode;
      gameEvents.emit(GameEvents.HUD_TOAST, {
        message: this._soloMode
          ? "Solo mode — Tab switches active cat."
          : "Local co-op — Minna: WASD+Space+F. Basil: Arrows+Shift/Enter+comma.",
        color: "#c49b3c",
      });
    }
    this.keys[key] = true;
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys[this.normalizeKey(e.key)] = false;
  };

  private normalizeKey(key: string): string {
    return key.toLowerCase();
  }
}
